/**
 * Command-palette model (OPSFL-POLISH-1) — the PURE half: item shapes, deterministic fuzzy
 * scoring, grouping and the recent-items ring. No React, no I/O, no AntD, so every ranking rule
 * is unit-testable and reproducible.
 *
 * Why subsequence scoring rather than a fuzzy-search dependency: an operator types 3–5 characters
 * under time pressure and must be able to PREDICT what lands first ("wb" → Workbench, every time).
 * A deterministic, boundary-aware subsequence score gives that; a black-box ranker does not — and
 * it costs zero new npm dependencies in a Tier-1 supply chain.
 */

export type PaletteItemKind = 'surface' | 'action' | 'flow' | 'entity';

export interface PaletteItem {
  /** Stable identity — also the recent-items key. Convention: `<kind>:<id>`. */
  id: string;
  kind: PaletteItemKind;
  /** The primary line the operator reads and matches against. */
  label: string;
  /** Secondary line: what it is / what it does. Matched at a lower weight. */
  hint?: string;
  /** Extra searchable text that is NOT displayed as the label (route, flow id, aliases). */
  keywords?: string;
  /** Navigation target for `surface` / `flow` / `entity` items (and route-only actions). */
  route?: string;
  /** Imperative behaviour for `action` items (help panel, tour). Takes precedence over `route`. */
  run?: () => void;
  /** Rendered on the right of the row (e.g. a status tag or the raw id). */
  meta?: string;
}

export interface PaletteGroup {
  key: string;
  title: string;
  items: PaletteItem[];
}

/** Group headings, in the fixed order the palette renders them. Order = triage priority. */
export const GROUP_ORDER = ['recent', 'surface', 'action', 'flow', 'entity'] as const;
export type GroupKey = (typeof GROUP_ORDER)[number];

export const GROUP_TITLE: Record<GroupKey, string> = {
  recent: 'Recent',
  surface: 'Go to',
  action: 'Actions',
  flow: 'Flows',
  entity: 'Entities',
};

// ---- scoring ------------------------------------------------------------------------------------

export interface FuzzyMatch {
  score: number;
  /** Indices in `text` that matched, in order — used to bold the matched characters. */
  positions: number[];
}

function isBoundary(text: string, index: number): boolean {
  if (index === 0) return true;
  const prev = text[index - 1];
  return !/[a-z0-9]/i.test(prev);
}

/**
 * Case-insensitive subsequence match with boundary/consecutive bonuses. Returns null when `query`
 * is not a subsequence of `text` (a hard filter — the palette never shows a row that does not
 * contain what was typed).
 *
 * Scoring (all deterministic, no randomness, no recency inside the match itself):
 *   +40 exact equality · +25 contiguous substring · +18 match at index 0
 *   +12 match at a word boundary · +10 match immediately after the previous match
 *   −1 per skipped character (capped at −20) · −0.05 × length (shorter labels win ties)
 */
function scan(q: string, t: string, preferBoundary: boolean): FuzzyMatch | null {
  const positions: number[] = [];
  let score = 0;
  let ti = 0;
  let skipped = 0;
  let lastMatch = -2;

  for (let qi = 0; qi < q.length; qi += 1) {
    const ch = q[qi];
    let found = -1;
    // Boundary pass: prefer a word-start occurrence of this character over the very next one, so
    // "ss" reaches "Screen Studio" rather than burning both letters inside "Screen".
    for (let i = ti; i < t.length; i += 1) {
      if (t[i] !== ch) continue;
      if (found === -1) found = i;
      if (preferBoundary && isBoundary(t, i)) {
        found = i;
        break;
      }
      if (!preferBoundary) break;
    }
    if (found === -1) return null;
    skipped += found - ti;
    if (found === 0) score += 18;
    else if (isBoundary(t, found)) score += 12;
    if (found === lastMatch + 1) score += 10;
    positions.push(found);
    lastMatch = found;
    ti = found + 1;
  }

  if (t === q) score += 40;
  if (t.includes(q)) score += 25;
  score -= Math.min(skipped, 20);
  score -= t.length * 0.05;
  return { score, positions };
}

export function fuzzyMatch(query: string, text: string): FuzzyMatch | null {
  const q = query.trim().toLowerCase();
  const t = text.toLowerCase();
  if (q.length === 0) return { score: 0, positions: [] };
  if (q.length > t.length) return null;
  // Two deterministic passes: the boundary-preferring one gives the better-reading match, but it
  // can strand a later character (jumping to "config"'s c leaves no r for "screen"). The plain
  // leftmost pass is the safety net, so a genuine subsequence is NEVER reported as a non-match.
  return scan(q, t, true) ?? scan(q, t, false);
}

/** Field weights: what the operator SEES ranks above what merely describes the row. */
const FIELD_WEIGHT = { label: 1, keywords: 0.75, hint: 0.5 } as const;

export interface ScoredItem {
  item: PaletteItem;
  score: number;
  /** Matched positions in the LABEL only (the sole highlighted field). */
  positions: number[];
}

/** Score one item across its searchable fields; null when nothing matches. */
export function scoreItem(query: string, item: PaletteItem): ScoredItem | null {
  const label = fuzzyMatch(query, item.label);
  const keywords = item.keywords ? fuzzyMatch(query, item.keywords) : null;
  const hint = item.hint ? fuzzyMatch(query, item.hint) : null;
  if (!label && !keywords && !hint) return null;
  const score = Math.max(
    label ? label.score * FIELD_WEIGHT.label : -Infinity,
    keywords ? keywords.score * FIELD_WEIGHT.keywords : -Infinity,
    hint ? hint.score * FIELD_WEIGHT.hint : -Infinity,
  );
  return { item, score, positions: label?.positions ?? [] };
}

/**
 * Rank a flat item list against the query. Ties break on the ORIGINAL order (stable), so an empty
 * query renders the authored order rather than an arbitrary permutation.
 */
export function rankItems(query: string, items: readonly PaletteItem[], limit = 8): ScoredItem[] {
  const scored: { s: ScoredItem; i: number }[] = [];
  items.forEach((item, i) => {
    const s = scoreItem(query, item);
    if (s) scored.push({ s, i });
  });
  scored.sort((a, b) => (b.s.score === a.s.score ? a.i - b.i : b.s.score - a.s.score));
  return scored.slice(0, limit).map((x) => x.s);
}

export interface BuildGroupsInput {
  query: string;
  surfaces: readonly PaletteItem[];
  actions: readonly PaletteItem[];
  flows: readonly PaletteItem[];
  entities: readonly PaletteItem[];
  /** Most-recently-used first; shown ONLY on the empty query (a memory, not a search result). */
  recents: readonly PaletteItem[];
  /** Cap for the open-ended groups (actions, flows, entities). Surfaces are never capped. */
  limitPerGroup?: number;
}

/**
 * Compose the rendered groups. Empty groups are dropped so the operator never scrolls past a
 * heading with nothing under it (Hick's Law: fewer visible choices, faster decision).
 *
 * Group ORDER: with no query the authored order (recents → go to → actions → flows → entities),
 * which is the "where can I go" reading. With a query the groups sort by their BEST match, so an
 * exact flow name outranks a surface that merely happens to contain those letters — typing the
 * name of the thing you want must put that thing under the cursor, not a near-miss above it.
 * Ties fall back to the authored order, so the ranking is still fully deterministic.
 */
export function buildGroups(input: BuildGroupsInput): PaletteGroup[] {
  const limit = input.limitPerGroup ?? 8;
  const q = input.query.trim();
  const head: PaletteGroup[] = [];
  const ranked: { group: PaletteGroup; best: number; order: number }[] = [];

  if (q.length === 0 && input.recents.length > 0) {
    head.push({ key: 'recent', title: GROUP_TITLE.recent, items: input.recents.slice(0, 5) });
  }
  const push = (key: GroupKey, items: readonly PaletteItem[], cap = limit) => {
    const scored = rankItems(q, items, cap);
    if (scored.length === 0) return;
    ranked.push({
      group: { key, title: GROUP_TITLE[key], items: scored.map((s) => s.item) },
      best: scored[0].score,
      order: GROUP_ORDER.indexOf(key),
    });
  };
  // The surface registry is a CLOSED list of ~15: showing all of them is the promise of the
  // palette ("go to any surface"), and truncating it would hide a destination that exists.
  push('surface', input.surfaces, input.surfaces.length || 1);
  push('action', input.actions);
  push('flow', input.flows);
  push('entity', input.entities);

  if (q.length > 0) ranked.sort((a, b) => (b.best === a.best ? a.order - b.order : b.best - a.best));
  return [...head, ...ranked.map((r) => r.group)];
}

/** Flatten groups to the linear list the arrow keys walk (what you see is the traversal order). */
export function flattenGroups(groups: readonly PaletteGroup[]): PaletteItem[] {
  return groups.flatMap((g) => g.items);
}

// ---- recent items ------------------------------------------------------------------------------

export const RECENTS_KEY = 'ol.palette.recents.v1';
export const RECENTS_MAX = 8;

/** A recent entry is a self-contained item: it must still be usable before any API responds. */
export type RecentItem = Pick<PaletteItem, 'id' | 'kind' | 'label' | 'hint' | 'route' | 'meta'>;

/** Move-to-front, de-duplicated on id, capped. Pure — the store just persists the result. */
export function pushRecent(recents: readonly RecentItem[], item: RecentItem): RecentItem[] {
  return [item, ...recents.filter((r) => r.id !== item.id)].slice(0, RECENTS_MAX);
}

/** Defensive parse of the persisted ring: a corrupt/foreign value degrades to "no recents". */
export function parseRecents(raw: string | null): RecentItem[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r): r is RecentItem => {
        if (typeof r !== 'object' || r === null) return false;
        const c = r as Partial<RecentItem>;
        return typeof c.id === 'string' && typeof c.label === 'string' && typeof c.kind === 'string';
      })
      .slice(0, RECENTS_MAX);
  } catch {
    return [];
  }
}
